import { AppUser, checkClaimsWeb, IOptionsWithTypes } from '@eventr/common'
import {
    ColoursObj,
    MobileConfirmDialog,
    TDialogStore,
    Typography,
    useDialogStore,
} from '@eventr/common-storybook'
import { Box } from '@mui/material'
import { useMemo, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { IconWithPopover } from '../../../common/iconWithPopover'
import { DataAPI, IGeneralInfo } from '../../../data/Data'
import i18n from '../../../i18n/i18next'
import { bs, useBoundStore } from '../../../state/bound.store'
import { loadingSignal } from '../../../state/overlayState/overlaySignals'
import { useRouteGuardContext } from '../../../state/routeGuardProvider'
import { NavigationView } from '../../layout/NavigationView'
import { ToolbarItemProps } from '../../layout/TopToolbar'
import { useEditMembers } from './editMembersProvider'
import { MembersList } from './membersList'

export const EditMembers = () => {
    const { wpUser, users, getSectionV2FromSectionIdFromSelectedItinerary } =
        useBoundStore(
            useShallow((s) => ({
                wpUser: s.wpUser,
                users: s.users,
                getSectionV2FromSectionIdFromSelectedItinerary:
                    s.getSectionV2FromSectionIdFromSelectedItinerary,
            }))
        )
    const { isAllowed } = useRouteGuardContext()

    const { removeMember } = useEditMembers()
    const { replaceDialog, clearDialog }: TDialogStore = useDialogStore(
        useShallow((s: TDialogStore) => ({
            clearDialog: s.clearDialog,
            replaceDialog: s.replaceDialog,
        }))
    )

    const eventInfoV2 = bs().getEventInfoV2()
    const safeMembers = () => {
        if (
            eventInfoV2 &&
            eventInfoV2.data &&
            eventInfoV2.data.members &&
            eventInfoV2.data.members.length > 0
        ) {
            return eventInfoV2.data.members
        } else {
            return []
        }
    }

    const [membersListSelect, setMembersListSelect] = useState<
        IOptionsWithTypes[]
    >(safeMembers())

    const anyPendingMemberChanges = useMemo(() => {
        if (
            JSON.stringify(membersListSelect) === JSON.stringify(safeMembers())
        ) {
            return false
        } else {
            return true
        }
    }, [membersListSelect, safeMembers()])

    const eventTitle = () => {
        if (eventInfoV2 && eventInfoV2.data && eventInfoV2.data.title) {
            return eventInfoV2.data.title
        } else {
            return ''
        }
    }

    const userControlBox = (user: AppUser) => {
        if (checkClaimsWeb(['/'], ['deleteUser']) && user.id !== wpUser.id) {
            return (
                <Box
                    onClick={(e) => {
                        //ignores the click from the parent
                        e.stopPropagation()
                    }}
                >
                    <IconWithPopover
                        iconName="Kebab"
                        actions={[
                            {
                                child: (
                                    <Typography color={ColoursObj.states.error}>
                                        {i18n.t(
                                            'edit-members.delete-user.delete-user'
                                        )}
                                    </Typography>
                                ),
                                callback: () => {
                                    replaceDialog({
                                        node: (
                                            <MobileConfirmDialog
                                                text={i18n.t(
                                                    'edit-members.delete-user.check-delete-user'
                                                )}
                                                header={i18n.t(
                                                    'edit-members.delete-user.delete-user'
                                                )}
                                                confirmText={i18n.t(
                                                    'general.remove'
                                                )}
                                                confirmBtnStyle="destructiveBorderless"
                                                onConfirm={async () => {
                                                    loadingSignal.value = true
                                                    await removeMember(user.id)
                                                    clearDialog()
                                                    loadingSignal.value = false
                                                }}
                                                onCancel={() => {
                                                    clearDialog()
                                                }}
                                            />
                                        ),
                                        backdropClosable: false,
                                    })
                                },
                            },
                        ]}
                    />
                </Box>
            )
        } else {
            return null
        }
    }

    const onBackPressed = () => {
        anyPendingMemberChanges
            ? replaceDialog({
                  node: (
                      <MobileConfirmDialog
                          text={i18n.t(
                              'edit-itinerary.item.unsaved-booking-text'
                          )}
                          header={i18n.t('edit-itinerary.item.pending-changes')}
                          confirmText={i18n.t('general.yes')}
                          cancelText={i18n.t('general.cancel')}
                          onConfirm={handleDialogConfirm}
                          onCancel={() => {
                              clearDialog()
                          }}
                      />
                  ),
                  backdropClosable: false,
              })
            : handleDialogConfirm()
    }

    const onSave = async () => {
        /// Should open modal ???? 'are you sure'

        loadingSignal.value = true

        /// Selected are to be deleted:
        if (eventInfoV2 && eventInfoV2.data) {
            /// already members list
            const newMembersList = membersListSelect
            setMembersListSelect([])

            var eventGeneralInfo =
                getSectionV2FromSectionIdFromSelectedItinerary(1)
            eventGeneralInfo.data.members = newMembersList

            const generalInfo: IGeneralInfo = {
                itinerary_id: bs().selectedItinID,
                section_id: 1,
                value: eventGeneralInfo.data,
            }

            await DataAPI.createUpdateGeneralInfo(generalInfo)
            loadingSignal.value = false

            // Should go back to selected page.
            clearDialog()
            window.goBack()
        } else {
            /// error?
            loadingSignal.value = false
        }
    }

    const handleDialogConfirm = () => {
        clearDialog()
        window.goBack()
    }

    const actions = () => {
        if (anyPendingMemberChanges) {
            return {
                id: 'saveButton',
                buttonText: i18n.t('general.done'),
                action: () => {
                    if (anyPendingMemberChanges) {
                        onSave()
                    }
                },
                useButton: true,
            } as ToolbarItemProps
        } else {
            return {
                id: 'addUserButton',
                icon: 'Add_User',
                action: () => {
                    window.pushPath('/setup/add-users')
                },
                disabled: !isAllowed(['allowedInviteUsers']),
            } as ToolbarItemProps
        }
    }

    return (
        <NavigationView
            usePageTransition
            navigationBarTitle={i18n.t('edit-members.edit-itin-members')}
            backButtonAction={onBackPressed}
            navigationBarSubtitle={eventTitle()}
            navigationBarTrailing={[actions()]}
        >
            <Box padding={'0px 21px'}>
                <MembersList
                    users={users}
                    wpUser={wpUser}
                    selectedUsers={membersListSelect}
                    userControlBox={userControlBox}
                    setSelectedUsers={setMembersListSelect}
                    onRefresh={async () => {
                        await Promise.all([
                            bs().getProfile(),
                            bs().fetchUsers(),
                            bs().forceItineraryMapUpdate(),
                        ])
                    }}
                />
            </Box>
        </NavigationView>
    )
}
